import axios from 'axios'
import React, { useState, useContext, useEffect, useMemo } from 'react'
import Button from '../../../button/Button'
import DropDown from 'src/components/dropDown/Dropdown'
import Input from '../../../input/Input'
import { baseApiURL } from 'src/utils/utils'
import { IBranch, IState, IVouchers } from 'src/Interface'
import { StateContext } from '../../../../AppContext'
import styles from './add-voucher.module.scss'
import { isValidNumberForRegion } from 'libphonenumber-js'
import { toast } from 'react-toastify'
import TextArea from 'src/components/textArea/TextArea'
import { add, differenceInDays } from 'date-fns'
import CustomDropdown from 'src/components/dropDown/CustomDropdown'
import { gaEvenActions, gaEvent, gaEventCategories } from 'src/utils/ga/ga'
import { gaTaxibonMapper, gaUserMapper } from 'src/utils/ga/gaMapper'


interface IErrors {
  phone: boolean
  reference: boolean
  name: boolean
}

interface IValues {
  name: string
  number: number
  language: 'DK' | 'EN'
  phone: string
  reference?: string
}

interface Props {
  close: () => void
  branchId: number
  voucher?: IVouchers
  branch: IBranch
  fetchVouchers: () => void
}

const AddVoucher: React.FunctionComponent<Props> = ({ branchId, close, voucher, branch, fetchVouchers = () => null }: Props) => {
  const state: IState = useContext(StateContext)
  const [isLoading, setIsLoading] = useState(false)
  const [expiringDate, setExpireDate] = useState(1)
  const validReference = /^.{1,50}$/
  const [validationErrors, setValidationErrors] = useState<Array<IErrors>>([{
    phone: false,
    reference: false,
    name: false
  }])
  const [values, setValues] = useState<Array<IValues>>([{
    name: '',
    language: 'DK',
    number: 1,
    phone: undefined,
    reference: null
  }])

  const dates = useMemo(() => {
    return [
      {
        label: '24 Timer',
        value: '1'
      },
      {
        label: '7 dage',
        value: '7'
      },
      {
        label: '1 måned',
        value: '30'
      },
      {
        label: '3 måned',
        value: '90'
      },
      {
        label: '6 måned',
        value: '180'
      },
    ]
  }, [])

  useEffect(() => {
    if (voucher) {
      setValues([{
        number: undefined,
        reference: voucher.reference,
        name: voucher.voucherCustomerName,
        phone: voucher.mobileUser.phone,
        language: voucher.language
      }])
      const days = differenceInDays(new Date(voucher.expiryDate), new Date)
      if (!dates.find(date => Number(date.value) === days)) {
        const years = Math.floor(days / 365);
        let remainingDays = days % 365; //Calculates the remaining days
        const months = Math.floor(remainingDays / 30);
        remainingDays = days % 30
        dates.push({
          label: `${years ? `${years} år` : ''}${years && months && remainingDays ? ',' : years && months && !remainingDays ? ' og' : ''} ${months ? `${months} måned` : ''} ${(years || months) && remainingDays ? 'og' : ''} ${remainingDays > 1 ? `${remainingDays} dage` : remainingDays ? `${remainingDays} dag` : ''}`,
          value: days.toString()
        })
        dates.sort(function (a, b) {
          return Number(a.value) - Number(b.value);
        })
        setExpireDate(days)
      }
    }
  }, [dates, voucher])

  const resetError = (fieldName: string, index: number) => {
    const validationErrorsCopy: any = [...validationErrors]

    if (validationErrorsCopy[index]) {
      validationErrorsCopy[index][fieldName] = false
    } else {
      validationErrorsCopy[index] = { [fieldName]: false }
    }
    setValidationErrors(validationErrorsCopy)
  }

  const handleInputChange = (fieldName: any, index) => (value: any) => {
    const valuesCopy = [...values]
    if (valuesCopy[index]) {
      valuesCopy[index][fieldName] = fieldName === 'number' ? Number(value) : value
    }
    setValues(valuesCopy)
  }

  const handleValidation = (regexp: RegExp, fieldName: 'phone' | 'reference', index) => (value: string) => {
    let error = false

    const errorsCopy: any = [...validationErrors]
    if (fieldName === 'phone') {
      if (!isValidNumberForRegion(value, 'DK')) {
        error = true
      }
    } else if (!regexp.test(value)) {
      error = true
    }

    if (errorsCopy[index]) {
      errorsCopy[index][fieldName] = error
    } else {
      errorsCopy.push({
        [fieldName]: error
      })
    }
    setValidationErrors(errorsCopy)
  }

  const createVoucher = () => {
    if (values.filter(value => !value.name || !value.phone || !value.reference).length) {
      const validationErrorsCopy = [...validationErrors]
      values.forEach((val, index) => {
        validationErrorsCopy[index].name = !val.name ? true : false
        validationErrorsCopy[index].phone = !val.phone ? true : false
        validationErrorsCopy[index].reference = !val.reference ? true : false
      })
      
      setValidationErrors(validationErrorsCopy)
    } else {
      setIsLoading(true)
      axios.post(`${baseApiURL()}/branches/${branchId}/vouchers`,
        {
          expiringDate: add(new Date(), { days: expiringDate }),
          users: values,
        }, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      }).then((result) => {
        result.data.forEach(taxibonId => gaEvent(gaEventCategories.taxibon, gaEvenActions.created_taxibon, gaUserMapper(state), gaTaxibonMapper(taxibonId)))
        fetchVouchers()
        close()
        setIsLoading(false)
        toast(<p className='tw-flex'>
          <svg xmlns="http://www.w3.org/2000/svg" className="tw-h-5 tw-w-5 tw-mr-4" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          <span>Taxibon oprettet</span>
        </p>)
      }).catch(err => {
        setIsLoading(false)
        toast(err.response.data.message)
      })
    }
  }

  const editVoucher = () => {
    setIsLoading(true)
    axios.put(`${baseApiURL()}/branches/${branchId}/vouchers/${voucher.id}`,
      {
        expiringDate: expiringDate ? add(new Date(), { days: expiringDate }) : voucher.expiryDate,
        reference: values[0].reference,
        language: values[0].language,
        voucherCustomerName: values[0].name
      }, {
      headers: {
        Authorization:
          `Bearer ${state.user.accessToken}`
      }
    }).then(() => {
      close()
      fetchVouchers()
      setIsLoading(false)
    }).catch(err => {
      setIsLoading(false)
      toast(err.response.data.message)
    })
  }

  const deleteRow = (index) => {
    const valuesCopy = [...values]
    const errorsCopy = [...validationErrors]
    errorsCopy.splice(index, 1)
    valuesCopy.splice(index, 1)
    setValidationErrors(errorsCopy)
    setValues(valuesCopy)
  }

  const clearData = () => {
    setValues([{
      name: '',
      number: 1,
      phone: '',
      language: 'DK',
      reference: null
    }])
    setValidationErrors([{ phone: false, reference: false, name: false }])
  }

  const handleCustomDropChange = (name, index) => {
    const valuesCopy = [...values]
    const userPhoneNumber = branch.users.find(user => user.name === name)?.phone
    if (valuesCopy[index]) {
      valuesCopy[index].name = name
      if (userPhoneNumber) {
        valuesCopy[index].phone = userPhoneNumber
      }
    }
    setValues(valuesCopy)
  }

  return (
    <div className={styles.addVoucher}>
      <header className={styles.header}>
        <h4>{voucher ? 'Rediger taxibon' : `Opret taxibon ${branch ? `til ${branch.label}` : ''}`}</h4>
        <button className={styles.resetButton} onClick={clearData}>Nulstil</button>
      </header>
      <div className={`${styles.section}`}>
        <DropDown isSearchable={false} label='Gyldig i' handleOnChange={setExpireDate} options={dates} value={expiringDate} />
      </div>
      <hr className='divider' />
      <div className={`${styles.section} tw-flex-auto`}>
        <div className='tw-flex tw-items-center'>
          <h6 className='tw-font-medium'>Kunde informationer</h6>
          {!voucher && <Button size='small' label='Tilføj modtager' type='tertiaryDark' modifiers='tw-ml-auto' handleClick={() => {
            setValues([...values, {
              name: '',
              number: 1,
              language: 'DK',
              phone: undefined,
              reference: null
            }])
            setValidationErrors([...validationErrors, { phone: false, reference: false, name: false }])
          }} />}
        </div>
        {values.map((value, index) => (
          <div className={styles.customerInfo} key={index}>
            <div className='tw-flex tw-items-center tw-mb-3'>
              <p className={styles.label}>Modtager {index + 1}</p>
              {index > 0 && <button className='tw-ml-auto' onClick={() => deleteRow(index)}><svg className='tw-w-4 tw-h-4' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              </button>}
            </div>
            <div className='tw-flex tw-mb-4'>
              <CustomDropdown
                list={branch && branch.users ? branch.users.map((user) => user.name) : []}
                label='Navn *'
                modifiers='tw-flex-5 tw-mr-3'
                name='name'
                error={validationErrors[index].name}
                placeholder='Firma / Navn'
                curried={false}
                resetError={() => resetError('name', index)}
                handleOnChange={(name) => handleCustomDropChange(name, index)}
                value={values[index].name}
              />
              {!voucher && <Input value={value.number} min={1} handleInputChange={() => handleInputChange('number', index)} label='Antal *' modifiers='tw-flex-1' type='number' name='quantity' />}
            </div>
            <div className='tw-flex tw-mb-3'>
              <Input modifiers='tw-flex-auto tw-mr-3' readOnly={!!voucher} resetError={() => resetError('phone', index)} error={validationErrors[index] ? validationErrors[index].phone : false} value={value.phone} handleValidation={() => handleValidation(null, 'phone', index)} handleInputChange={() => handleInputChange('phone', index)} label='Telefon *' type='text' isAllowOnlyDigits name='phone' placeholder='Indtast nr.' countryCode='+45' />
              <DropDown modifiers='tw-flex-auto' curried={true} label='Sprog *' options={[{ label: 'Dansk', value: 'DK' }, { label: 'Engelsk', value: 'EN' }]} handleOnChange={() => handleInputChange('language', index)} value={value.language} />
            </div>
            <TextArea resetError={() => resetError('reference', index)} error={validationErrors[index].reference} value={value.reference} handleValidation={() => handleValidation(validReference, 'reference', index)} handleInputChange={() => handleInputChange('reference', index)} label='Reference *' name='name' />
          </div>
        ))}
      </div>
      <footer className={styles.footer}>
        <Button type='secondary' label='Annuller' handleClick={close} />
        <Button disabled={!!validationErrors.filter(err => err.phone || err.reference || err.name).length} loading={isLoading} type='primary' label={voucher ? 'Gem ændringer' : 'Opret taxibon'} handleClick={voucher ? editVoucher : createVoucher} />
      </footer>
    </div >
  )
}

export default AddVoucher